/* eslint-disable prettier/prettier */
import { LoanTerm } from '@frontend/pricing-search';
import Common from '../../../common/services/Common';
import {
  AssetDocumentationLevelIdentifierType,
  AttachmentType,
  CommunityLendingProductType,
  ConstructionLoanType,
  IncomeDocumentationLevelIdentifierType,
  LoanProgramIdentifierType,
  PropertyUsageType,
} from './constants';
import {
  getAmortizationType,
  getCeilingRatePercent,
  getConstructionMethodTypeOtherDescription,
  getCreditReferenceType,
  getFinancedUnitCount,
  getLoanPurposeType,
  getProjectLegalStructureType,
  lookupType,
  omitPropertyIfNullOrUndefined,
} from './helpers';

export const buildMIDRQPayload = async ({
  loanData,
  rateData,
  loanTerms,
  encompassClientId,
  encompassUserId,
}: {
  loanData: any;
  rateData: any;
  loanTerms: LoanTerm[];
  encompassClientId: string;
  encompassUserId: string;
}) => {
  const originId = await Common.getOriginId();
  const { loanInformation = {}, borrowers = [], property = {} } = loanData;

  const primaryBorrower = borrowers[0] ?? {};
  const amortizationType = getAmortizationType(
    loanTerms,
    rateData?.programTypeID,
  );
  return {
    originId,
    encompassClientId,
    encompassUserId,
    loanGuid: loanData?.customId,
    miDetailedRateQuoteRequest: {
      customData: {
        loanAmortizationPeriodType: amortizationType,
        amortizationType,
        ...lookupType(
          AssetDocumentationLevelIdentifierType,
          'assetDocumentationLevelIdentifier',
          loanData?.borrowerFinancialHistory?.docTypeAsset,
        ),
        ...lookupType(AttachmentType, 'attachmentType', property?.type),
        bankruptcyIndicator:
          loanData?.borrowerFinancialHistory?.bankruptcyInMonths === 0 ||
          loanData?.borrowerFinancialHistory?.bankruptcyInMonths === 1,
        baseLoanAmount:
          loanInformation.lienPosition === 1
            ? loanInformation.firstMortgageAmount
            : loanInformation.secondMortgageAmount ?? 0,
        baseLTVRatioPercent: (rateData?.ltv ?? 0) * 100,
        buydownChangeFrequencyMonthsCount: rateData?.adjPeriod,
        ...getCeilingRatePercent(rateData?.lifetimeCap, rateData?.rate),
        combinedLtvRatioPercent: (rateData?.cltv ?? 0) * 100,
        ...lookupType(
          CommunityLendingProductType,
          'communityLendingProductType',
          Math.abs(rateData?.flagFilter ?? 0),
        ),
        constructionLoanIndicator:
          loanInformation?.loanPurpose === 4 ||
          loanInformation?.loanPurpose === 5,
        ...lookupType(
          ConstructionLoanType,
          'constructionLoanType',
          loanInformation?.loanPurpose,
        ),
        creditScoreModelNameType: 'ClassicFico',
        ...getConstructionMethodTypeOtherDescription(property?.type),
        ...getCreditReferenceType(
          loanData?.borrowerFinancialHistory?.noCreditScores,
        ),
        employmentBorrowerSelfEmployedIndicator:
          loanInformation?.selfEmployedBorrower,
        employmentDocumentationLevelIdentifier:
          loanData?.borrowerFinancialHistory?.docTypeEmployment === '0'
            ? 'NeitherStatedNorVerified'
            : 'StatedAndVerified',
        financedUnitCount: getFinancedUnitCount(property?.type),
        housingExpense: [
          {
            expenseType: 'FirstMortgagePrincipalAndInterest',
            paymentAmount: rateData?.pnI,
            timingType: 'Proposed',
          },
          {
            expenseType: 'HomeownersInsurance',
            paymentAmount: (property?.insuranceAmount ?? 0) / 12,
            timingType: 'Proposed',
          },
          {
            expenseType: 'RealEstateTax',
            paymentAmount: (property?.tax ?? 0) / 12,
            timingType: 'Proposed',
          },
        ],
        ...lookupType(
          IncomeDocumentationLevelIdentifierType,
          'incomeDocumentationLevelIdentifier',
          loanData?.borrowerFinancialHistory?.docTypeIncome,
        ),
        lienPriorityType:
          loanInformation.lienPosition === 1 ? 'FirstLien' : 'SecondLien',
        loanAmortizationPeriodCount: rateData?.termMonths,
        loanLevelCreditScoreValue: String(
          primaryBorrower?.borrowerFinanancial?.creditScore ?? '',
        ),
        ...lookupType(
          LoanProgramIdentifierType,
          'loanProgramIdentifier',
          Math.abs(rateData?.flagFilter ?? 0),
        ),
        ...getLoanPurposeType(
          loanInformation.loanPurpose,
          loanInformation?.constructionRefinance,
        ),
        marginRatePercent: rateData?.margin,
        mortgageType: 'Conventional',
        perChangeAdjustmentRules: [
          {
            adjustmentRuleType: 'First',
            perChangeMaximumIncreaseRatePercent: rateData?.firstCap,
            perChangeRateAdjustmentFrequencyMonthsCount: rateData?.firstAdjPeriod,
          },
          {
            adjustmentRuleType: 'Subsequent',
            perChangeMaximumIncreaseRatePercent: rateData?.periodicCap,
            perChangeRateAdjustmentFrequencyMonthsCount: rateData?.adjPeriod,
          },
        ],
        ...omitPropertyIfNullOrUndefined(
          'paymentAdjustmentLifetimeCapPercent',
          rateData?.lifetimeCap,
        ),
        prepaymentPenaltyIndicator:
          loanInformation?.prepaymentPenaltyTerm !== 0,
        priorPropertyForeclosureCompletedIndicator:
          loanData?.borrowerFinancialHistory?.noticeOfDefaultForeClosure !==
          null,
        ...getProjectLegalStructureType(property?.type),
        propertyEstimatedValueAmount: property?.value,
        ...lookupType(PropertyUsageType, 'propertyUsageType', property?.use),
        pudIndicator: property?.type === 13 || property?.type === 15,
        ...omitPropertyIfNullOrUndefined(
          'rentalEstimatedGrossMonthlyRentAmount',
          property?.rentalIncome,
        ),
        ...omitPropertyIfNullOrUndefined(
          'salesConcessionAmount',
          primaryBorrower?.sellerDollar,
        ),
      },
    },
  };
};
